import Button from "components/UI/Button";
import { HookForm } from "components/UI/HookForm/Form";
import { HookInput } from "components/UI/HookForm/Form/Input";
import { HookReactSingleSelect } from "components/UI/HookForm/Form/ReactSelect";
import { HookTextarea } from "components/UI/HookForm/Form/TextArea";
import { HookFormProvider } from "components/UI/HookForm/Provider";
import Modal from "components/UI/Modal";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { IPostApplicationFormServicePayload } from "api/Rover/ApplicationForm/types";
import { TApplicationFormType } from "features/ApplicationForm/types";
import { useDispatch, useSelector } from "react-redux";
import { workspaceSelector } from "store";
import { notifyUser } from "store/Toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  patchApplicationForm,
  postApplicationForm,
} from "api/Rover/ApplicationForm";
import { RoverReactQueryKey } from "constants/ReactQueryKeys";
import { sanitizeResponseError } from "utils";
import { HookFileInput } from "components/UI/HookForm/Form/File";
import useUpload from "../../../../hooks/useUpload";
import { IPatchApplicationFormServicePayload } from "api/Rover/ApplicationForm/types";
import { IPostProjectAssetPayload } from "api/Sherpa/Project/modules/ProjectAsset/types";
import { useUsers } from "hooks/ReactQueryHooks/useUsersQuery";
import { User } from "types/User";

const validationSchema = Yup.object({
  name: Yup.string()
    .required("Required")
    .min(2, "Full Name must be greater than 2 characters")
    .max(150, "Full Name must be less than 25 characters"),
  formType: Yup.object({
    label: Yup.string(),
    value: Yup.string(),
  }).test("formType", "You must select a valid form type.", (val) =>
    val.value ? true : false
  ),
  // leadById: Yup.object({
  //   label: Yup.string(),
  //   value: Yup.string(),
  // }).test('leadById', 'You must select a valid form lead.', (val) =>
  //   val.value ? true : false
  // ),
  description: Yup.string().max(250, "max character limit is 250"),
  logo: Yup.mixed().nullable(),
});

export const applicationFormTypes = [
  { label: "Client Application", value: "CLIENT_APPLICATION" },
  { label: "Partner Proposal", value: "PARTNER_PROPOSAL" },
];

interface INewApplicationFormData {
  name: string;
  formType: { label: string; value: string };
  // leadById?: { label: string; value: number };
  description?: string;
  logo: File[] | null;
}

interface INewApplicationFormHookForm {
  handleModal: () => void;
}

const NewApplicationFormHookForm = ({
  handleModal,
}: INewApplicationFormHookForm) => {
  const dispatch = useDispatch();
  const { currentWorkspace } = useSelector(workspaceSelector);
  const uploadApplicationLogo = useUpload();

  const queryClient = useQueryClient();

  const fetchUsers = useUsers({ params: { active: "true" } });

  const users = fetchUsers.data?.data;

  const authorizedProjectLeadRoles = [
    "SUPER ADMIN",
    "ADMIN",
    "PROJECT MANAGER",
  ];

  const authorizedProjectLeadUsers: User[] = [];

  users?.forEach((user: User) => {
    if (authorizedProjectLeadRoles.includes(user.role.toUpperCase())) {
      authorizedProjectLeadUsers.push(user);
    }
  });

  const validLeadOptions = authorizedProjectLeadUsers.map(
    ({ id, name, email }) => ({
      label: `${name} <${email}>`,
      value: id,
    })
  );

  const updateApplicationForm = useMutation(
    ({
      data,
      applicationId,
    }: {
      data: Pick<IPatchApplicationFormServicePayload, "logo">;
      applicationId: string;
    }) => patchApplicationForm({ data, applicationFormId: applicationId }),
    {
      onSuccess: () => {},
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: "failed",
          })
        );
      },
    }
  );

  const addApplicationForm = useMutation(
    (payload: { data: IPostApplicationFormServicePayload; logo: File[] }) =>
      postApplicationForm({ data: payload?.data }),
    {
      onSuccess: (res, payload) => {
        if (payload.logo) {
          uploadApplicationLogo.uploadFile({
            file: payload.logo[0],
            resource: "applicationForm",
            resourceId: res?.data?.id,
            path: "thumbnails",
            applyData: (signedFiles: any) => {
              const data: Omit<IPostProjectAssetPayload, "phase"> = {
                name: payload.logo[0].name,
                key: signedFiles.key,
                contentType: signedFiles.type,
                assetType: "image",
                size: signedFiles.size,
              };
              updateApplicationForm.mutate({
                data: { logo: data },
                applicationId: res?.data.id,
              });
            },
          });
        }

        queryClient.invalidateQueries([
          RoverReactQueryKey.GET_APPLICATION_FORMS,
        ]);
        handleModal();
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `${payload.data.name} application form created successfully.`,
            toastType: "success",
          })
        );
      },
      onError: (error) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: "failed",
          })
        );
        handleModal();
      },
    }
  );

  const onSubmit = (formData: INewApplicationFormData) => {
    const { logo, ...rest } = formData;
    debugger;
    const data: IPostApplicationFormServicePayload = {
      ...rest,
      formType: formData?.formType?.value as TApplicationFormType,
      // leadById: formData?.leadById?.value,
      workspaceId: Number(currentWorkspace?.id),
    };

    if (!currentWorkspace || !currentWorkspace?.id) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage:
            "Please select a program to create an application form.",
          toastType: "failed",
        })
      );
    } else {
      addApplicationForm.mutate({ data: data, logo: logo! });
    }
  };

  return (
    <HookForm onSubmit={onSubmit} className="space-y-4">
      <HookInput id="name" name="name" type="text" label="Name" />
      <HookReactSingleSelect
        id="form-type"
        options={applicationFormTypes}
        placeholder="Select Form Type"
        name="formType"
        label="Form Type"
      />
      {/* <HookReactSingleSelect
        id='form-lead'
        options={validLeadOptions}
        placeholder='Select Form Lead'
        name='leadById'
        label='Form Lead'
      /> */}
      <HookTextarea id="description" name="description" label="Description" />
      <HookFileInput id="logo" name="logo" label="Logo" />
      <div className="flex gap-2 justify-end mt-5">
        <Button label="Cancel" btnStyle="neutral" onClick={handleModal} />
        <Button label="Submit" btnStyle="primary" btnType="submit" />
      </div>
    </HookForm>
  );
};

export const NewApplicationForm = (props: INewApplicationFormHookForm) => {
  return (
    <>
      <Modal>
        <h2 className="mt-0 capitalize">New Application Form</h2>
        <HookFormProvider resolver={yupResolver(validationSchema)} mode="all">
          <NewApplicationFormHookForm {...props} />
        </HookFormProvider>
      </Modal>
    </>
  );
};
